import React from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import LeadGenerationOption from "../LeadGenerationOption/LeadGenerationOption";
import ServiceTitanIcon from "../ServiceTitanIcon/ServiceTitanIcon";
import { Integration } from "../../../hooks/useIntegrations";

interface LeadGenerationMethodProps {
  activeIntegration?: Integration;
  canSwitchIntegration: boolean;
  isQuickBooksConfigured: boolean;
  isQuickBooksConnected: boolean;
  isServiceTitanConfigured: boolean;
  isServiceTitanMappingComplete: boolean;
  onSelect: (integration: Integration) => void;
}

const LeadGenerationMethod: React.FC<LeadGenerationMethodProps> = ({
  activeIntegration,
  canSwitchIntegration,
  isQuickBooksConfigured,
  isQuickBooksConnected,
  isServiceTitanConfigured,
  isServiceTitanMappingComplete,
  onSelect,
}) => {
  const resolveServiceTitanSourceHint = () => {
    if (!isServiceTitanConfigured) return "Connect ServiceTitan first";
    if (!isServiceTitanMappingComplete)
      return "Finish ServiceTitan setup first";
    return undefined;
  };

  const resolveQuickBooksSourceHint = () => {
    if (!isQuickBooksConnected) return "Connect QuickBooks first";
    if (!isQuickBooksConfigured) return "Finish QuickBooks setup first";
    return undefined;
  };

  const resolveLockReason = (integration: Integration, sourceHint?: string) => {
    if (integration === activeIntegration) return undefined;
    // Imported data outranks setup state — finishing setup would not unlock
    // the option, so pointing at setup would send the user down a dead end.
    if (!canSwitchIntegration) return "Contact support to switch integration";
    if (sourceHint) return sourceHint;
    return undefined;
  };

  return (
    <div className="space-y-3">
      <LeadGenerationOption
        description="Import customers or invoices from a CSV, XLSX, or XLS file. No connection required."
        icon={<ArrowDownTrayIcon className="h-4 w-4" />}
        isSelected={activeIntegration === "file_import"}
        lockReason={resolveLockReason("file_import")}
        name="Upload a list"
        onSelect={() => onSelect("file_import")}
        tag="Always available"
      />

      <LeadGenerationOption
        description="Generate leads directly from your synced ServiceTitan customer records."
        icon={<ServiceTitanIcon className="h-4 w-4" />}
        isSelected={activeIntegration === "servicetitan"}
        lockReason={resolveLockReason(
          "servicetitan",
          resolveServiceTitanSourceHint(),
        )}
        name="ServiceTitan customers"
        onSelect={() => onSelect("servicetitan")}
      />

      <LeadGenerationOption
        description="Generate leads from your imported QuickBooks customer records."
        icon={<span className="text-xs font-bold text-[#2ca01c]">qb</span>}
        isSelected={activeIntegration === "quickbooks_online"}
        lockReason={resolveLockReason(
          "quickbooks_online",
          resolveQuickBooksSourceHint(),
        )}
        name="QuickBooks customers"
        onSelect={() => onSelect("quickbooks_online")}
      />
    </div>
  );
};

export default LeadGenerationMethod;
